'use client';

import { c as _c } from "react/compiler-runtime";
import { type Choice, ChoiceList, Heading } from '@jaaq/hub-ui';
import { Building2, CircleHelp, Link } from 'lucide-react';
import type { ReactElement } from 'react';
import { OnboardingShell } from '#components/onboarding-shell';
import type { IconKey } from '#lib/content/types';
import { useOnboardingContent, useOnboardingState } from '#lib/onboarding-provider';
import type { Outcome } from '#lib/steps/state';

/**
 * Step 1/3 — outcome question (multi-select). Copy + choice list come from
 * the content layer; only the icon registry lives here (icons are visual
 * primitives the CMS shouldn't own).
 */
export const OutcomeStep = () => {
  const $ = _c(20);
  const {
    onboardingState,
    patch,
    goNext
  } = useOnboardingState();
  const {
    outcome
  } = useOnboardingContent();
  const {
    outcomes: t0
  } = onboardingState;
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const outcomes = t1;
  let t2;
  if ($[2] !== outcome.choices) {
    t2 = outcome.choices.map(_temp);
    $[2] = outcome.choices;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const choices = t2;
  let t3;
  if ($[4] !== outcomes || $[5] !== patch) {
    t3 = id_0 => patch({
      outcomes: outcomes.includes(id_0) ? outcomes.filter(selected => selected !== id_0) : [...outcomes, id_0]
    });
    $[4] = outcomes;
    $[5] = patch;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const toggleOutcome = t3;
  const t4 = outcomes.length > 0;
  let t5;
  if ($[7] !== outcome.heading) {
    t5 = <Heading level={1} variant="display" tone="inverse">{outcome.heading}</Heading>;
    $[7] = outcome.heading;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== outcomes || $[10] !== toggleOutcome) {
    t6 = {
      multiple: true,
      selectedIds: outcomes,
      onToggle: toggleOutcome
    };
    $[9] = outcomes;
    $[10] = toggleOutcome;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== choices || $[13] !== t6) {
    t7 = <ChoiceList choices={choices} selection={t6} />;
    $[12] = choices;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== goNext || $[16] !== t4 || $[17] !== t5 || $[18] !== t7) {
    t8 = <OnboardingShell canContinue={t4} onContinue={goNext}>{t5}{t7}</OnboardingShell>;
    $[15] = goNext;
    $[16] = t4;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};

/* ----- icon registry: maps content `iconKey` → SVG ----- */

type IconProps = {
  name: IconKey;
};
const Icon = t0 => {
  const $ = _c(3);
  const {
    name
  } = t0;
  switch (name) {
    case "building":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Building2 className="size-6" />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "link":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Link className="size-6" />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "question":
      {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <CircleHelp className="size-6" />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
};
function _temp(t0) {
  const {
    id,
    title,
    description,
    iconKey
  } = t0;
  return {
    id,
    title,
    description,
    icon: <Icon name={iconKey} />
  };
}